import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { connect } from "react-redux";
import PropType from "prop-types";
import { css } from "@emotion/core";
import { Checkbox, Icon } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import {
  getAllScheduleTemplate,
  getFolders,
  getScheduleTemplate,
} from "../../services/api";
import { ClipLoader } from "react-spinners";
import {
  assignScheduleTemplateToFoldersOrServices,
  getSchedulingServicesMiddleware,
  setSchedulingServiceData,
} from "../../store/middlewares/servicesMiddleware";
import StatusIconService from "../services/StatusIconService";
import { isEmptyObject, isNotAnEmptyArray } from "../../helpers/helpers";
import ButtonWithSpinner from "../HelperComponents/ButtonWithSpinner";
import { clearAssignTemplateStatus } from "../../store/actions/serviceAction";

// Can be a string as well. Need to ensure each key-value pair ends with ;
const override = css`
  display: block;
  margin: 0 auto;
  border-color: #7bc807;
`;

const AssignTemplateModal = (props) => {
  const {
    showModal,
    toggleModal,
    scheduleTemplate,
    assignmentType,
    allServices,
    onConnectPcoModal,

    // Redux props
    schedulingServicesData,
    assignTemplateStatus,

    // Redux functions
    getSchedulingServices,
    setSchedulingServiceData,
    assignScheduleTemplateToFoldersOrServices,
    clearAssignTemplateStatus,
  } = props;
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [serviceListIds, setServiceListIds] = useState([]);
  const [folderId, setFolderId] = useState("");
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [ScheduleTemplateId, setScheduleTemplateId] = useState("");
  const [teamPage, setTeamPage] = useState(scheduleTemplate);
  const [teams, setTeams] = useState(() =>
    Array.isArray(scheduleTemplate?.teams)
      ? scheduleTemplate.teams
      : scheduleTemplate?.teams?.data || []
  );
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState("");
  const loadedTeamOffsets = useRef(new Set([0]));
  const requestedTeamOffsets = useRef(new Set());
  const teamsScrollContainer = useRef(null);
  const teamsScrollSentinel = useRef(null);
  const initiallySelectedTeamIds = useMemo(() => {
    if (Array.isArray(scheduleTemplate?.selectedTeamIds)) {
      return scheduleTemplate.selectedTeamIds.map(String);
    }
    if (Array.isArray(scheduleTemplate?.teamIds)) {
      return scheduleTemplate.teamIds.map(String);
    }
    if (Array.isArray(scheduleTemplate?.teamList)) {
      return scheduleTemplate.teamList.map((team) =>
        String(typeof team === "object" ? team.id : team)
      );
    }
    const initialTeams = Array.isArray(scheduleTemplate?.teams)
      ? scheduleTemplate.teams
      : scheduleTemplate?.teams?.data || [];
    return initialTeams
      .filter(
        (team) =>
          team.checked || team.selected || team.is_selected || team.isSelected
      )
      .map((team) => String(team.id));
  }, [scheduleTemplate]);
  const [teamIds, setTeamIds] = useState(initiallySelectedTeamIds);
  const [initialTeamIds, setInitialTeamIds] = useState(
    initiallySelectedTeamIds
  );
  const isTeamAssignment = assignmentType === "teams";
  const teamPagination = teamPage?.meta || teamPage?.teams?.meta || {};
  const hasNextTeamPage =
    teamPagination.next !== null && teamPagination.next !== undefined;
  const teamsByService = useMemo(
    () =>
      teams.reduce((services, team) => {
        const relationship =
          team?.relationships?.serviceTypes ||
          team?.relationships?.service_types ||
          team?.relationships?.service_type;
        const relationshipData = relationship?.data || relationship;
        const serviceTypes = Array.isArray(relationshipData)
          ? relationshipData
          : relationshipData
          ? [relationshipData]
          : [{ id: "unassigned" }];

        serviceTypes.forEach((serviceType) => {
          const serviceId = serviceType?.id || "unassigned";
          const service = allServices.find(
            (item) => String(item.id) === String(serviceId)
          );

          if (!services[serviceId]) {
            services[serviceId] = {
              id: serviceId,
              title:
                service?.title ||
                service?.attributes?.name ||
                service?.attributes?.title ||
                serviceType?.attributes?.name ||
                serviceType?.attributes?.title ||
                (serviceId === "unassigned"
                  ? "Unassigned service"
                  : `Service ${serviceId}`),
              teams: [],
            };
          }
          if (
            !services[serviceId].teams.some(
              ({ id }) => String(id) === String(team.id)
            )
          ) {
            services[serviceId].teams.push(team);
          }
        });
        return services;
      }, {}),
    [allServices, teams]
  );
  const [expandedTeamServices, setExpandedTeamServices] = useState(() =>
    Object.keys(teamsByService).reduce((expandedServices, serviceId) => {
      expandedServices[serviceId] = true;
      return expandedServices;
    }, {})
  );

  useEffect(() => {
    setExpandedTeamServices((currentServices) => {
      const updatedServices = { ...currentServices };
      Object.keys(teamsByService).forEach((serviceId) => {
        if (updatedServices[serviceId] === undefined) {
          updatedServices[serviceId] = true;
        }
      });
      return updatedServices;
    });
  }, [teamsByService]);

  const location = useLocation();

  const disabledButton = useMemo(() => {
    if (isTeamAssignment) {
      return (
        teamIds.length === initialTeamIds.length &&
        teamIds.every((id) => initialTeamIds.includes(id))
      );
    }
    const ids = Object.values(scheduleTemplate?.serviceList)
      ?.filter(Array.isArray)
      ?.flat();
    const allIncluded =
      ids?.every((id) => serviceListIds?.includes(id)) &&
      ids?.length === serviceListIds?.length;
    if (allIncluded) return true;
    return false;
  }, [folders, serviceListIds, isTeamAssignment, teamIds, initialTeamIds]);

  useEffect(() => {
    if (assignTemplateStatus === "success") {
      toggleModal();
    }
  }, [assignTemplateStatus, clearAssignTemplateStatus, toggleModal]);

  useEffect(() => {
    setTeamPage(scheduleTemplate);
    setTeams(
      Array.isArray(scheduleTemplate?.teams)
        ? scheduleTemplate.teams
        : scheduleTemplate?.teams?.data || []
    );
    setTeamIds(initiallySelectedTeamIds);
    setInitialTeamIds(initiallySelectedTeamIds);
    loadedTeamOffsets.current = new Set([0]);
    requestedTeamOffsets.current = new Set();
    setTeamsError("");
  }, [scheduleTemplate, initiallySelectedTeamIds]);

  const getPaginationOffset = useCallback((page) => {
    if (page === null || page === undefined) return null;
    const rawOffset = typeof page === "object" ? page.offset : page;
    const offset = Number(rawOffset);
    return Number.isFinite(offset) && offset >= 0 ? offset : null;
  }, []);

  const loadTeamPage = useCallback(
    async (page) => {
      const offset = getPaginationOffset(page);
      if (
        offset === null ||
        teamsLoading ||
        loadedTeamOffsets.current.has(offset) ||
        requestedTeamOffsets.current.has(offset)
      )
        return;

      const scheduleObj = queryString.parse(location.search);
      const templateId =
        scheduleObj?.id || scheduleTemplate?.template?.id || ScheduleTemplateId;
      if (!templateId) return;

      try {
        requestedTeamOffsets.current.add(offset);
        setTeamsLoading(true);
        setTeamsError("");
        const result = await getScheduleTemplate(templateId, offset);
        setTeamPage(result);

        if (!loadedTeamOffsets.current.has(offset)) {
          const pageTeams = Array.isArray(result?.teams)
            ? result.teams
            : result?.teams?.data || [];
          setTeams((currentTeams) => {
            const teamIds = new Set(
              currentTeams.map((team) => String(team.id))
            );
            return [
              ...currentTeams,
              ...pageTeams.filter((team) => !teamIds.has(String(team.id))),
            ];
          });
          const selectedIds = pageTeams
            .filter(
              (team) =>
                team.checked ||
                team.selected ||
                team.is_selected ||
                team.isSelected
            )
            .map((team) => String(team.id));
          setTeamIds((currentIds) => [
            ...new Set([...currentIds, ...selectedIds]),
          ]);
          setInitialTeamIds((currentIds) => [
            ...new Set([...currentIds, ...selectedIds]),
          ]);
          loadedTeamOffsets.current.add(offset);
        }
      } catch (error) {
        setTeamsError(error?.message || "Unable to load teams");
      } finally {
        requestedTeamOffsets.current.delete(offset);
        setTeamsLoading(false);
      }
    },
    [
      ScheduleTemplateId,
      getPaginationOffset,
      location.search,
      scheduleTemplate,
      teamsLoading,
    ]
  );

  const handleTeamsScroll = useCallback(
    (event) => {
      if (!isTeamAssignment || !hasNextTeamPage || teamsLoading) return;
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight <= 120) {
        loadTeamPage(teamPagination.next);
      }
    },
    [
      hasNextTeamPage,
      isTeamAssignment,
      loadTeamPage,
      teamPagination.next,
      teamsLoading,
    ]
  );

  useEffect(() => {
    const sentinel = teamsScrollSentinel.current;
    if (!isTeamAssignment || !sentinel || !hasNextTeamPage) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !teamsLoading) {
          loadTeamPage(teamPagination.next);
        }
      },
      {
        root: null,
        rootMargin: "150px",
      }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    hasNextTeamPage,
    isTeamAssignment,
    loadTeamPage,
    teamPagination.next,
    teamsLoading,
  ]);

  const handleGetFolders = useCallback(async () => {
    try {
      setFoldersLoading(true);
      setErrorMessage("");
      const defaultFolder = {
        id: 1,
        status: "close",
        title: "Upcoming Services",
      };
      const currentFolders = await getFolders();
      if (currentFolders) {
        currentFolders.unshift(defaultFolder);
        const initialExpandedFolders = {};
        const appendChecked = currentFolders?.map((folder) => {
          folder.checked = scheduleTemplate?.folderList[folder?.title] ?? false;
          initialExpandedFolders[folder.id] = true;
          getSchedulingServices(folder?.id);
          if (scheduleTemplate?.folderList?.hasOwnProperty(folder?.title)) {
            const ids = Object.values(scheduleTemplate?.serviceList)
              ?.filter(Array.isArray)
              ?.flat();
            setServiceListIds(ids);
          }
          return folder;
        });
        setExpandedFolders(initialExpandedFolders);
        setFolders(appendChecked);
      }
      setFoldersLoading(false);
    } catch (error) {
      setFoldersLoading(false);
      setErrorMessage(error?.message);
    }
  }, []);

  const getAllScheduleTemplates = async () => {
    const templates = await getAllScheduleTemplate();
    if (templates?.length) {
      setScheduleTemplateId(
        templates.find((template) => template.is_default)?.id
      );
    }
  };

  useEffect(() => {
    handleGetFolders();
  }, [handleGetFolders]);

  useEffect(() => {
    if (onConnectPcoModal) {
      getAllScheduleTemplates();
    }
  }, [onConnectPcoModal]);

  const onChangeFolderChecked = useCallback(
    (folder) => (event) => {
      const { checked } = event.target;
      setFolderId(folder?.id);
      if (!checked) {
        const checkedFolderServices = schedulingServicesData[
          folder?.id
        ]?.data.map((data) => data?.id);
        if (serviceListIds) {
          const updatedStateArray = serviceListIds?.filter(
            (id) => !checkedFolderServices?.includes(id)
          );
          setServiceListIds(updatedStateArray);
          const updatedScheduleService = {
            ...schedulingServicesData,
            [folder.id]: {
              isLoading: false,
              error: null,
            },
          };
          setFolderId("");
          setSchedulingServiceData(updatedScheduleService);
        }
      }

      const updatedFolder = folders.map((item) => {
        if (item.id === folder.id) {
          item.checked = checked;
        }
        return item;
      });
      if (checked) {
        setExpandedFolders((currentState) => ({
          ...currentState,
          [folder.id]: true,
        }));
        getSchedulingServices(folder.id);
      }
      setFolders(updatedFolder);
    },
    [folders, getSchedulingServices, schedulingServicesData]
  );

  const onToggleFolder = useCallback(
    (folder) => () => {
      const isExpanded = !!expandedFolders[folder.id];

      if (
        !isExpanded &&
        !schedulingServicesData[folder.id]?.data &&
        !schedulingServicesData[folder.id]?.isLoading
      ) {
        getSchedulingServices(folder.id);
      }

      setExpandedFolders((currentState) => ({
        ...currentState,
        [folder.id]: !isExpanded,
      }));
    },
    [expandedFolders, getSchedulingServices, schedulingServicesData]
  );

  const onChangeSchedulingService = useCallback(
    (schedulingService) => (event) => {
      const { checked } = event.target;
      const currentScheduling =
        schedulingServicesData[schedulingService.folderId]?.data;

      let updatedServiceList = [];
      if (checked) {
        updatedServiceList = [...serviceListIds, schedulingService?.id];
      } else {
        updatedServiceList = serviceListIds?.filter(
          (id) => id !== schedulingService?.id
        );
      }

      const allServiceIdsIncluded = currentScheduling?.every((item) =>
        updatedServiceList?.includes(item?.id)
      );
      const updatedFolder = folders?.map((item) => {
        if (item.id === schedulingService?.folderId) {
          item.checked = allServiceIdsIncluded;
        }
        return item;
      });

      setFolders(updatedFolder);
      setServiceListIds(updatedServiceList);

      const updatedScheduling = currentScheduling?.map((service) => {
        if (service.id === schedulingService?.id) {
          service.checked = checked;
        }
        return service;
      });

      const updatedScheduleService = {
        ...schedulingServicesData,
        [schedulingService.folderId]: {
          isLoading: false,
          error: null,
          data: updatedScheduling,
        },
      };

      setSchedulingServiceData(updatedScheduleService);
    },
    [schedulingServicesData, setSchedulingServiceData, serviceListIds, folders]
  );

  const getSelectedItemsIds = useCallback((data = []) => {
    return data.map((item) => item.id);
  }, []);

  const getSelectedServicesIds = useCallback((data = {}) => {
    if (isEmptyObject(data)) return [];
    const folderIds = Object.keys(data);
    const servicesIds = folderIds.map((folderId) => {
      const services = data[folderId];
      return services.map((service) => service.id);
    });
    return isNotAnEmptyArray(servicesIds) ? servicesIds.flat() : [];
  }, []);

  const onClickedAssign = useCallback(() => {
    const selectedFolders = folders?.filter((folder) => folder.checked);
    const folderIds = getSelectedItemsIds(selectedFolders);
    const scheduleObj = queryString?.parse(location?.search);
    const id = scheduleObj?.id ? scheduleObj?.id : ScheduleTemplateId;
    assignScheduleTemplateToFoldersOrServices(
      id,
      serviceListIds,
      folderIds,
      teamIds
    );
  }, [
    folders,
    getSelectedItemsIds,
    getSelectedServicesIds,
    location,
    assignScheduleTemplateToFoldersOrServices,
    schedulingServicesData,
    teamIds,
    isTeamAssignment,
  ]);

  const onChangeTeam = useCallback(
    (teamId) => (event) => {
      const id = String(teamId);
      setTeamIds((currentIds) =>
        event.target.checked
          ? [...new Set([...currentIds, id])]
          : currentIds.filter((currentId) => currentId !== id)
      );
    },
    []
  );

  const onToggleTeamService = useCallback((serviceId) => {
    setExpandedTeamServices((currentServices) => ({
      ...currentServices,
      [serviceId]: !currentServices[serviceId],
    }));
  }, []);

  useEffect(() => {
    const allServices = schedulingServicesData[folderId]?.data;
    const checkedFolder =
      folders?.find((folder) => folder?.id === folderId)?.checked ?? true;
    if (allServices && checkedFolder) {
      const serviceIds = serviceListIds;
      allServices?.map((service) => {
        if (!serviceListIds?.includes(service?.id)) {
          serviceIds?.push(service?.id);
        }
      });
      setServiceListIds([...serviceIds]);
    }
  }, [schedulingServicesData]);

  return (
    <Dialog
      open={showModal}
      onClose={toggleModal}
      aria-labelledby="form-dialog-title"
      className="assign-template-modal"
      classes={{
        paper: "vw-100",
      }}
      maxWidth="lg"
    >
      <DialogTitle id="form-dialog-title" className="pb-0">
        <p className="assign-template-modal-heading">
          <b>
            Assign the template ({scheduleTemplate?.template?.name}) to specific{" "}
            {isTeamAssignment ? "teams" : "folders and services"}
          </b>
        </p>
        <label className="text-muted assign-modal-label">
          {isTeamAssignment
            ? "Select the Planning Center teams that should use this template"
            : "Assigning to a folder will include all current and future plans automatically"}
        </label>
      </DialogTitle>
      <IconButton
        className="close-icon"
        style={{ position: "absolute", right: 0 }}
        onClick={toggleModal}
      >
        <Icon>close</Icon>
      </IconButton>
      <DialogContent
        ref={teamsScrollContainer}
        onScroll={isTeamAssignment ? handleTeamsScroll : undefined}
      >
        <div className={`${foldersLoading ? "overflow-hidden" : ""} mt-2`}>
          {!isTeamAssignment ? (
            <div>
              <div className="w-100 folders-heading-container">
                <h5 className="p-0 mb-1">
                  <strong>Folders</strong>
                </h5>
              </div>
              <div className="mt-2">
                {errorMessage && (
                  <label className="text-center text-danger">
                    {errorMessage}
                  </label>
                )}
                <ClipLoader
                  css={override}
                  sizeUnit={"px"}
                  size={80}
                  color={"#7BC807"}
                  loading={foldersLoading}
                />
                <div>
                  {!foldersLoading &&
                    folders &&
                    Array.isArray(folders) &&
                    Boolean(folders.length) &&
                    folders.map((folder, idx) => (
                      <div
                        className="assign-template-folder-container"
                        key={idx}
                      >
                        <div
                          className="assign-template-folder-list"
                          onClick={onToggleFolder(folder)}
                          style={{ cursor: "pointer" }}
                        >
                          {idx !== 0 && (
                            <Checkbox
                              className="checkbox"
                              checked={folder.checked}
                              onChange={onChangeFolderChecked(folder)}
                              onClick={(event) => event.stopPropagation()}
                              value="selectAll"
                              inputProps={{
                                "aria-label": "primary checkbox",
                              }}
                            />
                          )}
                          {folder.status === "open" && <Icon>folder_open</Icon>}
                          {folder.status === "close" && (
                            <Icon
                              className={`${idx === 0 ? "first-element" : ""}`}
                            >
                              folder
                            </Icon>
                          )}
                          <label className={`mt-2 ml-2`}>
                            {folder?.title || ""}
                          </label>
                          <div className="ml-auto">
                            <Icon>
                              {expandedFolders[folder.id]
                                ? "keyboard_arrow_up"
                                : "keyboard_arrow_down"}
                            </Icon>
                          </div>
                        </div>
                        {expandedFolders[folder.id] && (
                          <div className="schedule-services-container">
                            <div className="vertical-line"></div>
                            {schedulingServicesData[folder.id]?.isLoading && (
                              <div className="pb-2">
                                <ClipLoader
                                  css={override}
                                  sizeUnit={"px"}
                                  size={50}
                                  color={"#7BC807"}
                                  loading={
                                    schedulingServicesData[folder.id]?.isLoading
                                  }
                                />
                              </div>
                            )}
                            <div>
                              {schedulingServicesData[folder.id]?.data &&
                                Array.isArray(
                                  schedulingServicesData[folder.id]?.data
                                ) &&
                                Boolean(
                                  schedulingServicesData[folder.id]?.data
                                    ?.length
                                ) &&
                                schedulingServicesData[folder.id]?.data.map(
                                  (service, index) => (
                                    <div
                                      key={index}
                                      className="flexer-start schedule-service-list"
                                    >
                                      <div>
                                        <div className="horizontal-line"></div>
                                        <Checkbox
                                          className="checkbox"
                                          checked={serviceListIds.includes(
                                            service.id
                                          )}
                                          onChange={onChangeSchedulingService({
                                            ...service,
                                            folderId: folder.id,
                                          })}
                                          value="selectAll"
                                          inputProps={{
                                            "aria-label": "primary checkbox",
                                          }}
                                        />
                                      </div>
                                      <div className="d-flex align-items-start justify-content-center flex-column">
                                        <label className="text-muted m-0">
                                          {service?.title}{" "}
                                          <span className="ml-2">
                                            <span>
                                              {service.confirmed}/
                                              {service.total}
                                            </span>
                                            {service.status && (
                                              <StatusIconService
                                                status={service.status}
                                              />
                                            )}
                                          </span>
                                        </label>
                                        <label className="text-muted small-label">
                                          (
                                          {scheduleTemplate?.template?.name ||
                                            ""}
                                          ){" "}
                                        </label>
                                      </div>
                                    </div>
                                  )
                                )}

                              {schedulingServicesData[folder.id]?.data &&
                                Array.isArray(
                                  schedulingServicesData[folder.id]?.data
                                ) &&
                                !Boolean(
                                  schedulingServicesData[folder.id]?.data
                                    ?.length
                                ) && (
                                  <div className="text-center text-muted mt-2">
                                    <h4>No Services found</h4>
                                  </div>
                                )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  {!foldersLoading &&
                    folders &&
                    Array.isArray(folders) &&
                    !Boolean(folders.length) && (
                      <div className="text-center text-muted">
                        <h4>No folders found</h4>
                      </div>
                    )}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="w-100 folders-heading-container">
                <h5 className="p-0 mb-1">
                  <strong>Teams</strong>
                </h5>
              </div>
              <div className="mt-2">
                {teamsError && (
                  <label className="d-block text-center text-danger">
                    {teamsError}
                  </label>
                )}
                {teams.length ? (
                  Object.values(teamsByService).map((service) => (
                    <div
                      className="assign-template-folder-container"
                      key={service.id}
                    >
                      <div
                        className="assign-template-folder-list"
                        onClick={() => onToggleTeamService(service.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <Icon>event_note</Icon>
                        <label className="mt-2 ml-2">{service.title}</label>
                        <div className="ml-auto">
                          <Icon>
                            {expandedTeamServices[service.id]
                              ? "keyboard_arrow_up"
                              : "keyboard_arrow_down"}
                          </Icon>
                        </div>
                      </div>
                      {expandedTeamServices[service.id] && (
                        <div className="schedule-services-container">
                          <div className="vertical-line"></div>
                          {service.teams.map((team) => (
                            <div
                              className="flexer-start schedule-service-list"
                              key={team.id}
                            >
                              <div>
                                <div className="horizontal-line"></div>
                                <Checkbox
                                  className="checkbox"
                                  checked={teamIds.includes(String(team.id))}
                                  onChange={onChangeTeam(team.id)}
                                  inputProps={{
                                    "aria-label": `Select ${
                                      team?.attributes?.name || "team"
                                    }`,
                                  }}
                                />
                              </div>
                              <div className="d-flex align-items-start justify-content-center flex-column">
                                <label className="text-muted m-0">
                                  {team?.attributes?.name || `Team ${team.id}`}
                                </label>
                                <label className="text-muted small-label">
                                  ({scheduleTemplate?.template?.name || ""})
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : !teamsLoading ? (
                  <div className="text-center text-muted mt-3">
                    <h4>No teams found</h4>
                  </div>
                ) : null}
                {hasNextTeamPage && (
                  <div ref={teamsScrollSentinel} style={{ height: 20 }} />
                )}
                <div className="py-3">
                  <ClipLoader
                    css={override}
                    sizeUnit={"px"}
                    size={40}
                    color={"#7BC807"}
                    loading={teamsLoading}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions className="flexer-space-end p-4">
        <div>
          <ButtonWithSpinner
            classes={{
              root: "btn assign-folder-btn",
              containedPrimary: "contained-primary",
            }}
            onClick={onClickedAssign}
            disabled={
              disabledButton ||
              foldersLoading ||
              assignTemplateStatus === "loading"
            }
            loading={assignTemplateStatus === "loading"}
          >
            Assign
          </ButtonWithSpinner>
        </div>
      </DialogActions>
    </Dialog>
  );
};

AssignTemplateModal.propTypes = {
  toggleModal: PropType.func,
  showModal: PropType.bool,
  scheduleTemplate: PropType.object,
  assignmentType: PropType.oneOf(["folders", "teams"]),
  allServices: PropType.array,
};

AssignTemplateModal.defaultProps = {
  toggleModal: () => {},
  showModal: false,
  scheduleTemplate: {},
  assignmentType: "folders",
  allServices: [],
};

const mapStateToProps = (store) => {
  return {
    schedulingServicesData: store.services.schedulingServicesData,
    assignTemplateStatus: store.services.assignTemplateStatus,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getSchedulingServices: (folderId) =>
    dispatch(getSchedulingServicesMiddleware(folderId)),
  setSchedulingServiceData: (data) => dispatch(setSchedulingServiceData(data)),
  clearAssignTemplateStatus: () => dispatch(clearAssignTemplateStatus()),
  assignScheduleTemplateToFoldersOrServices: (
    id,
    serviceIds,
    folderIds,
    teamIds
  ) =>
    dispatch(
      assignScheduleTemplateToFoldersOrServices(
        id,
        serviceIds,
        folderIds,
        teamIds
      )
    ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AssignTemplateModal);
